import { useEffect, useRef, useState } from "react";
import Dialog from "@mui/material/Dialog";
import IconButton from "@mui/material/IconButton";
import styled, { keyframes } from "styled-components";
import { useNavigate } from "react-router-dom";
import { mediaUrl, refreshMediaToken } from "../../../mediaAuth";
import { markGiftViewedApi, reactToGiftApi } from "../../../giftsApi";
import { isIOS, canPlayOnIOS, videoMime } from "../../../utils";
import { logVideoPlaybackError } from "../../../videoDiagnostics";
import { WaveformBars } from "../chat/WaveformBars";
import type { Gift } from "../../../types";

const DROP_REACTIONS: { emoji: string; name: string }[] = [
  { emoji: "❤️", name: "heart" },
  { emoji: "🔥", name: "fire" },
  { emoji: "😘", name: "kiss" },
];

// One-time entrance for the reveal moment itself (V2 Part C) - distinct
// from FireGlow/Flame's own continuous ambient pulse above, which keeps
// running throughout. prefers-reduced-motion swaps the scale+fade for a
// plain opacity fade - what's shown is identical either way, only the
// motion changes.
const revealIn = keyframes`
  from { opacity: 0; transform: scale(0.92); }
  to { opacity: 1; transform: scale(1); }
`;
const revealInReduced = keyframes`
  from { opacity: 0; }
  to { opacity: 1; }
`;

// Same fullscreen-stage pattern as Lightbox.tsx (bypasses AppDialog's .glass
// styling on purpose) but deliberately simpler - no next/prev nav, no
// favorite/delete actions, since a "once" gift is a single, disposable view.
// Closing IS consuming, but only once the media has actually rendered
// (mediaLoaded below): markGiftViewedApi() is what deletes the file
// server-side (GiftsDB.markViewed()), not the media fetch itself - see
// server.js's GET /api/gifts/:id/media for why viewing needs to tolerate
// more than one request (video range requests) while still only ever
// counting as "viewed" once, on close. Gating on mediaLoaded means closing
// before the browser actually has pixels/frames to show (dropped
// connection, 404, closed mid-load) leaves the gift untouched instead of
// destroying a one-time gift the recipient never saw.
//
// The stage itself is transparent, not a solid black fill - MUI's own
// Backdrop already dims/blurs the page behind it, so a second, opaque black
// layer on top just read as a broken black rectangle around the heart. The
// only visuals inside the stage are the heart (or video) and the decorative
// fire ring around it.
//
// Photos render in the same heart-clipped frame as SendGiftDialog's
// preview, at an explicit fixed size - not width:100%/height:100% chained
// through .media-fs/.modal-body like the first version of this component
// (and like Lightbox.tsx's own gallery viewer) used. That relative-sizing
// chain depends on every ancestor in between actually resolving a definite
// height; here it silently didn't, collapsing the image to a sliver. A
// fixed-px heart frame can't collapse the same way. Video keeps a plain
// rectangular player instead of the heart clip - native <video controls>
// chrome clipped into a heart shape isn't usable.
const Stage = styled.div`
  background: transparent;
  height: 100%;
  margin: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 16px;
  padding: 16px;
`;

const MessageBubble = styled.p`
  color: #fff;
  text-align: center;
  max-width: min(420px, 85vw);
  margin: 0;
  padding: 10px 18px;
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.12);
  backdrop-filter: blur(6px);
  font-size: 0.95rem;
  line-height: 1.4;
  white-space: pre-wrap;
  overflow-wrap: break-word;
`;

// Sender name + "Unlocked {date} at {time}" - only ever rendered for a
// Private Drop (gift.deliveryMode === "unlock_later"), never for a plain
// View Once/Keep reveal, which stays pixel-identical to today.
const SenderMeta = styled.div`
  position: relative;
  text-align: center;
  color: rgba(255, 255, 255, 0.85);
`;

const SenderName = styled.div`
  font-weight: 700;
  font-size: 1.05rem;
  color: #fff;
`;

const UnlockedAt = styled.div`
  font-size: 0.78rem;
  color: rgba(255, 255, 255, 0.55);
  margin-top: 2px;
`;

const ReactionRow = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  gap: 10px;
`;

const ReactionButton = styled.button<{ $active: boolean }>`
  appearance: none;
  border: none;
  width: 44px;
  height: 44px;
  border-radius: 50%;
  font-size: 1.3rem;
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${(p) => (p.$active ? "rgba(255, 90, 154, 0.35)" : "rgba(255, 255, 255, 0.12)")};
  transition:
    transform 0.15s ease,
    background-color 0.15s ease;
  &:active {
    transform: scale(0.9);
  }
`;

const ReplyButton = styled.button`
  appearance: none;
  border: none;
  border-radius: 999px;
  padding: 8px 18px;
  font-size: 0.85rem;
  font-weight: 600;
  color: #fff;
  background: rgba(255, 255, 255, 0.14);
  display: inline-flex;
  align-items: center;
  gap: 6px;
`;

const VoiceCard = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 14px;
  width: min(340px, 85vw);
  padding: 28px 22px;
  border-radius: 24px;
  background: rgba(255, 255, 255, 0.08);
  backdrop-filter: blur(10px);
  animation: ${revealIn} 700ms cubic-bezier(0.16, 1, 0.3, 1);
  @media (prefers-reduced-motion: reduce) {
    animation: ${revealInReduced} 300ms ease-out;
  }
`;

const glow = keyframes`
  0%, 100% { opacity: 0.55; transform: scale(0.94); }
  50% { opacity: 0.95; transform: scale(1.06); }
`;

const flicker = keyframes`
  0%, 100% { opacity: 0.5; transform: translate(-50%, -50%) scale(0.85) rotate(-4deg); }
  50% { opacity: 1; transform: translate(-50%, -50%) scale(1.15) rotate(4deg); }
`;

const MediaFrame = styled.div`
  position: relative;
  width: min(320px, 80vw);
  height: min(320px, 80vw);
  animation: ${revealIn} 700ms cubic-bezier(0.16, 1, 0.3, 1);
  @media (prefers-reduced-motion: reduce) {
    animation: ${revealInReduced} 300ms ease-out;
  }
`;

const FireGlow = styled.div`
  position: absolute;
  inset: -18px;
  border-radius: 50%;
  filter: blur(22px);
  background: radial-gradient(circle, rgba(255, 150, 50, 0.85), rgba(255, 60, 60, 0.35) 60%, transparent 75%);
  animation: ${glow} 1.6s ease-in-out infinite;
  pointer-events: none;
`;

// Preset points tracing a rough heart outline (percent of MediaFrame) - a
// fixed layout rather than trig-computed positions, so it scales cleanly
// with the frame's own min(320px, 80vw) sizing at any viewport width.
const FLAME_POSITIONS = [
  { top: 6, left: 32 },
  { top: 3, left: 50 },
  { top: 6, left: 68 },
  { top: 20, left: 12 },
  { top: 20, left: 88 },
  { top: 42, left: 8 },
  { top: 42, left: 92 },
  { top: 62, left: 22 },
  { top: 62, left: 78 },
  { top: 80, left: 38 },
  { top: 80, left: 62 },
  { top: 95, left: 50 },
];

const Flame = styled.span<{ $top: number; $left: number; $delay: number }>`
  position: absolute;
  top: ${(p) => p.$top}%;
  left: ${(p) => p.$left}%;
  font-size: 1.35rem;
  line-height: 1;
  transform: translate(-50%, -50%);
  animation: ${flicker} 1.1s ease-in-out infinite;
  animation-delay: ${(p) => p.$delay}s;
  filter: drop-shadow(0 0 6px rgba(255, 120, 40, 0.85));
  pointer-events: none;
`;

function FireRing() {
  return (
    <>
      {FLAME_POSITIONS.map((pos, i) => (
        <Flame key={i} $top={pos.top} $left={pos.left} $delay={(i % 5) * 0.16}>
          🔥
        </Flame>
      ))}
    </>
  );
}

// Video playback audit, Bug D: same frozen-media-token failure mode
// MessageBubble.tsx's chat video already had to solve - a gift left
// unopened long enough for the media-auth token to expire previously just
// showed the permanent "can't play" fallback for what's really a stale
// token.
const MAX_VIDEO_AUTO_RETRIES = 2;

export function GiftViewerDialog({ gift, onClose }: { gift: Gift; onClose: (updatedGift: Gift | null) => void }) {
  const navigate = useNavigate();
  const isVirtual = gift.type === "virtual";
  const isPrivateDrop = gift.deliveryMode === "unlock_later";
  const [videoFailed, setVideoFailed] = useState(false);
  const [videoRetryKey, setVideoRetryKey] = useState(0);
  const videoRetryCountRef = useRef(0);
  // Optimistic-then-synced, same pattern as every other reaction surface
  // in this app - the server's gift_reaction WS push (to the sender) and
  // this call's own response both converge on the same array, so a brief
  // race just self-corrects on the next render. Reactions are recipient-
  // only (server-enforced) and this dialog only ever opens for a received
  // gift, so gift.recipientId IS the current user - no separate
  // appState.currentUserId exists in this app (only currentUserEmail).
  const [reactions, setReactions] = useState(gift.reactions);
  const myUserId = gift.recipientId;
  const myReaction = reactions.find((r) => r.userId === myUserId)?.emoji ?? null;

  // A brief, distinct pulse for the reveal moment itself (V2 Part C) - one-
  // shot on mount, never continuous. The reaction tap's own vibrate(15)
  // stays separate/lighter, so the two are still distinguishable.
  useEffect(() => {
    if (isPrivateDrop && navigator.vibrate) navigator.vibrate(25);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function handleReact(emoji: string): Promise<void> {
    const next = myReaction === emoji ? null : emoji;
    setReactions((prev) => [...prev.filter((r) => r.userId !== myUserId), ...(next ? [{ userId: myUserId, emoji: next }] : [])]);
    if (navigator.vibrate) navigator.vibrate(15);
    await reactToGiftApi(gift.id, next);
  }

  function handleReply(): void {
    // Contextual (V2 Part E): pre-fills the reply compose state to the
    // drop's own chat card message, reusing chat's existing replyTo
    // mechanism rather than inventing a second linkage model - snippet/
    // sender are already known here (no need to fetch the message back
    // from history just to reconstruct what this dialog already has). Old
    // drops (pre-V2, no chatMessageId) just land on a normal, un-prefilled
    // thread - same as before this feature.
    navigate(
      `/chat/${gift.conversationId}`,
      gift.chatMessageId
        ? { state: { replyTo: { messageId: gift.chatMessageId, senderName: gift.senderName, snippet: "[Private Drop]" } } }
        : undefined,
    );
  }
  // Closing used to always mark-viewed (and, for "once" gifts, delete the
  // file server-side) regardless of whether the media actually rendered -
  // a dropped connection, a 404, or just closing before it finished loading
  // would permanently destroy a one-of-a-kind gift the recipient never
  // actually saw. Only counts as "viewed" once the browser has confirmed it
  // has real pixels/frames to show (img onLoad / video onLoadedData) -
  // closing before that just closes, leaving the gift untouched to reopen.
  // A virtual gift has no async media to wait for (its "content" is the
  // catalog emoji, already rendered the instant this mounts), so it starts
  // already "loaded" - closing it always marks viewed.
  const [mediaLoaded, setMediaLoaded] = useState(isVirtual);
  const [voicePlaying, setVoicePlaying] = useState(false);

  async function handleClose(): Promise<void> {
    if (!mediaLoaded) {
      onClose(null);
      return;
    }
    const updated = await markGiftViewedApi(gift.id);
    onClose(updated);
  }

  // Virtual gift reveal - a separate, simpler branch (own Dialog) rather
  // than threading isVirtual through every line of the custom-gift render
  // below: there's no attachment, no img/video, no heart-clip/HEVC
  // concerns, no View Once file-deletion semantics (always mode "keep") -
  // just the catalog art + name in the same fire-ring/glow reveal
  // treatment, then the existing mark-viewed-on-close path.
  if (isVirtual) {
    return (
      <Dialog
        open
        fullScreen
        onClose={handleClose}
        container={() => document.getElementById("react-root")}
        slots={{ paper: Stage }}
        slotProps={{ backdrop: { sx: { backgroundColor: "rgba(20, 6, 4, 0.7)", backdropFilter: "blur(14px)" } } }}
      >
        <IconButton
          aria-label="Back"
          onClick={handleClose}
          sx={{
            position: "absolute",
            top: 16,
            left: 16,
            zIndex: 1,
            color: "#fff",
            background: "rgba(255,255,255,0.15)",
            "&:hover": { background: "rgba(255,255,255,0.25)" },
          }}
        >
          <i className="bi bi-arrow-left" />
        </IconButton>

        <MediaFrame style={{ width: "min(260px, 65vw)", height: "min(260px, 65vw)" }}>
          <FireGlow />
          <FireRing />
          <div
            style={{
              position: "relative",
              width: "100%",
              height: "100%",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontSize: "5.5rem",
            }}
            aria-hidden="true"
          >
            {gift.catalogEmoji || "🎁"}
          </div>
        </MediaFrame>
        <div style={{ position: "relative", color: "#fff", fontWeight: 700, fontSize: "1.3rem", textAlign: "center" }}>
          {gift.catalogName || "Gift"}
        </div>

        {gift.message && <MessageBubble>{gift.message}</MessageBubble>}
      </Dialog>
    );
  }

  if (!gift.attachment) return null;
  // Captured locally so the onError closure below still has TS's non-null
  // narrowing - it doesn't carry through into a nested function otherwise.
  const attachment = gift.attachment;
  const src = mediaUrl(`/api/gifts/${gift.id}/media`);
  const isVideo = attachment.type === "video";
  const isVoice = attachment.type === "audio";
  // Same HEVC-in-.mov reasoning as MessageBubble.tsx/Lightbox.tsx - can't
  // tell from the filename alone, so this also reacts to real playback
  // failure, not just the filename-based webm check.
  const videoUnplayable = isVideo && ((isIOS() && !canPlayOnIOS(gift.attachment.filename)) || videoFailed);
  // iOS Safari has a long-standing bug where clip-path: url(#id) referencing
  // an SVG clipPath with objectBoundingBox units, applied to an <img>/<video>
  // (not an SVG shape), renders unclipped or sometimes not at all - this is
  // the leading suspect for "photo received as gift doesn't show on iOS".
  // Falling back to a plain rounded frame there guarantees the gift is
  // actually visible, at the cost of the decorative heart shape.
  const heartClip = isIOS() ? undefined : "url(#giftHeartClipViewer)";

  return (
    <Dialog
      open
      fullScreen
      onClose={handleClose}
      container={() => document.getElementById("react-root")}
      slots={{ paper: Stage }}
      slotProps={{ backdrop: { sx: { backgroundColor: "rgba(20, 6, 4, 0.7)", backdropFilter: "blur(14px)" } } }}
    >
      <svg width="0" height="0" style={{ position: "absolute" }} aria-hidden="true">
        <defs>
          <clipPath id="giftHeartClipViewer" clipPathUnits="objectBoundingBox">
            <path d="M 0.5 0.85 C 0.2 0.6, 0 0.4, 0 0.25 C 0 0.1, 0.15 0, 0.28 0 C 0.38 0, 0.46 0.05, 0.5 0.15 C 0.54 0.05, 0.62 0, 0.72 0 C 0.85 0, 1 0.1, 1 0.25 C 1 0.4, 0.8 0.6, 0.5 0.85 Z" />
          </clipPath>
        </defs>
      </svg>

      <IconButton
        aria-label="Back"
        onClick={handleClose}
        sx={{
          position: "absolute",
          top: 16,
          left: 16,
          zIndex: 1,
          color: "#fff",
          background: "rgba(255,255,255,0.15)",
          "&:hover": { background: "rgba(255,255,255,0.25)" },
        }}
      >
        <i className="bi bi-arrow-left" />
      </IconButton>

      {isVoice ? (
        // iOS Safari can't play webm - same isIOS()/canPlayOnIOS() gate as
        // VoiceMessagePlayer.tsx (chat's own voice-note playback) already
        // uses, since MediaRecorder's webm output is otherwise unplayable
        // there regardless of this app's own code.
        <VoiceCard>
          <i className="bi bi-mic-fill" style={{ fontSize: "2.4rem", color: "#fff" }} aria-hidden="true" />
          <div style={{ color: "#fff", fontWeight: 600 }}>Just for you</div>
          {isIOS() && !canPlayOnIOS(attachment.filename) ? (
            <a href={src} target="_blank" rel="noopener" className="btn btn-pink btn-sm" onClick={() => setMediaLoaded(true)}>
              Open voice message
            </a>
          ) : (
            <>
              <audio
                id="giftViewerVoiceAudio"
                src={src}
                onCanPlay={() => setMediaLoaded(true)}
                onPlay={() => setVoicePlaying(true)}
                onPause={() => setVoicePlaying(false)}
                onEnded={() => setVoicePlaying(false)}
              />
              <button
                type="button"
                className="private-drop-voice-play"
                aria-label={voicePlaying ? "Pause" : "Play"}
                onClick={() => {
                  const el = document.getElementById("giftViewerVoiceAudio") as HTMLAudioElement | null;
                  if (!el) return;
                  if (voicePlaying) el.pause();
                  else el.play();
                }}
              >
                <i className={`bi ${voicePlaying ? "bi-pause-fill" : "bi-play-fill"}`} aria-hidden="true" />
              </button>
              <WaveformBars levels={new Array(32).fill(0.5)} />
            </>
          )}
        </VoiceCard>
      ) : isVideo ? (
        <MediaFrame style={{ width: "min(90vw, 480px)", height: "auto" }}>
          <FireGlow />
          {videoUnplayable ? (
            <div style={{ position: "relative", color: "#fff", textAlign: "center", padding: "2rem 1rem" }}>
              <div style={{ marginBottom: 8 }}>Can't play this video here.</div>
              <a href={src} target="_blank" rel="noopener" className="btn btn-pink btn-sm">
                Open it instead
              </a>
            </div>
          ) : (
            <video
              key={videoRetryKey}
              playsInline
              controls
              autoPlay
              muted
              style={{ maxWidth: "90vw", maxHeight: "90vh", position: "relative" }}
              onError={(e) => {
                logVideoPlaybackError("gift-viewer", e.currentTarget, attachment.filename);
                if (videoRetryCountRef.current < MAX_VIDEO_AUTO_RETRIES) {
                  videoRetryCountRef.current += 1;
                  refreshMediaToken().finally(() => setVideoRetryKey((k) => k + 1));
                } else {
                  setVideoFailed(true);
                }
              }}
              onLoadedData={() => setMediaLoaded(true)}
            >
              <source src={src} type={videoMime(gift.attachment.filename)} />
              Your browser does not support the video element.
            </video>
          )}
        </MediaFrame>
      ) : (
        <MediaFrame>
          <FireGlow />
          <FireRing />
          <img
            src={src}
            alt="Gift"
            onLoad={() => setMediaLoaded(true)}
            style={{
              position: "relative",
              width: "100%",
              height: "100%",
              objectFit: "cover",
              clipPath: heartClip,
              borderRadius: heartClip ? undefined : "24px",
            }}
          />
        </MediaFrame>
      )}

      {gift.message && <MessageBubble>{gift.message}</MessageBubble>}

      {/* Sender name/unlock timestamp/reactions/reply - a Private Drop
          reveal only (gift.deliveryMode === "unlock_later"). A plain View
          Once/Keep reveal stays exactly as it already was - neither of
          these existed in this dialog before this feature, and adding
          them unconditionally would change behavior for every gift, not
          just Private Drops. */}
      {isPrivateDrop && (
        <>
          <SenderMeta>
            <SenderName>{gift.senderName}</SenderName>
            {gift.unlockAt && (
              <UnlockedAt>
                Unlocked{" "}
                {new Date(gift.unlockAt).toLocaleDateString(undefined, { month: "short", day: "numeric" })} at{" "}
                {new Date(gift.unlockAt).toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" })}
              </UnlockedAt>
            )}
          </SenderMeta>

          <ReactionRow>
            {DROP_REACTIONS.map(({ emoji, name }) => (
              <ReactionButton key={emoji} type="button" $active={myReaction === emoji} aria-label={`React with ${name}`} onClick={() => handleReact(emoji)}>
                {emoji}
              </ReactionButton>
            ))}
            <ReplyButton type="button" aria-label="Reply to chat" onClick={handleReply}>
              <i className="bi bi-reply-fill" aria-hidden="true" />
              Reply
            </ReplyButton>
          </ReactionRow>
        </>
      )}
    </Dialog>
  );
}
